"use client";

import React, { useState, useRef } from "react";
import Turnstile from "react-turnstile";
import { Check } from "lucide-react";
import contactBg from "./assets/bg_contact.svg";

// Put your real site‑key here or read it from env
const SITE_KEY = "0x4AAAAAABhmYS-00ZwwBnzn";

export default function Contact() {
    /* form fields */
    const [name, setName]         = useState("");
    const [email, setEmail]       = useState("");
    const [company, setCompany]   = useState("");
    const [message, setMessage]   = useState("");
    const [investor, setInvestor] = useState(false);
    const [partner, setPartner]   = useState(false);

    /* ui state */
    const [token, setToken]           = useState("");      // Turnstile token
    const [isLoading, setIsLoading]   = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    /* ref so we can reset() the widget */
    const turnstileRef = useRef(null);

    /* --- helpers --- */
    const resetTurnstile = () => {
        if (turnstileRef.current) turnstileRef.current.reset();
        setToken("");
    };

    /* --- form submit --- */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!token) return alert("Please complete the security verification.");

        setIsLoading(true);
        try {
            const res = await fetch("/api/contact.php", {
                method : "POST",
                headers: { "Content-Type": "application/json" },
                body   : JSON.stringify({
                    name,
                    email,
                    company,
                    message,
                    investor,
                    partner,
                    turnstileToken: token
                })
            });
            const json = await res.json();

            if (res.ok && json.success) {
                setIsSubmitted(true);
                setTimeout(() => {
                    setIsSubmitted(false);
                    setName(""); setEmail(""); setCompany("");
                    setMessage(""); setInvestor(false); setPartner(false);
                    resetTurnstile();
                }, 3000);
            } else {
                alert(json.message || "Failed to send message. Please try again.");
                resetTurnstile();
            }
        } catch (err) {
            console.error(err);
            alert("Failed to send message. Please try again.");
            resetTurnstile();
        } finally {
            setIsLoading(false);
        }
    };

    /* --- jsx --- */
    return (
        <section
            id="contact"
            className="relative text-[0.834rem] pl-8 mt-[6.25rem] px-10 py-6 w-full mx-auto max-w-[101.25rem] min-h-[37.5rem] bg-white bg-left-bottom bg-no-repeat"
            style={{ backgroundImage: `url(${contactBg})` }}
        >
            <div className="grid md:grid-cols-2 gap-12 pt-4">
                {/* info column */}
                <div>
                    <h2 className="relative bg-black text-white w-fit p-2 text-4xl font-semibold">
                        Ready to build the future with us?
                    </h2>
                    <p className="max-w-[42.5rem] mt-10 text-2xl">
                        We’re actively seeking forward‑thinking investors and strategic partners to accelerate the deployment of our autonomous construction systems. If you share our vision for safer, faster, and smarter infrastructure development, we’d love to connect.
                    </p>
                </div>

                {/* form column */}
                <div className="bg-white/5 backdrop-blur-sm blueBorder md:p-6  min-h-[37.5rem] xl:mr-20">
                    {isSubmitted? (
                        <div className="text-left py-8 flex h-full items-center w-fit mx-auto ">

                            <div className="w-6 h-16 flex items-center justify-center mr-5 mb-4 ">
                                <svg xmlns="http://www.w3.org/2000/svg" width="26" height="20" viewBox="0 0 26 20" fill="none">
                                    <path d="M22.0159 0L9.39501 12.5065L5.72862 9.60692L3.52525 7.86012L0 12.2269L7.84391 18.4453L9.81806 20L26 3.96468L22.0159 0Z" fill="#FFEF25"/>
                                </svg>
                            </div>

                            <p className="w-fit">
                                Your message was successfully sent.<br />
                                We will contact you in the next 24-48 hours
                            </p>
                        </div>

                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-2">
                            {/* name */}
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                placeholder="Your full name"
                                className="w-full p-3 border border-blueBorder bg-transparent placeholder-placeHolder"
                            />

                            {/* email */}
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                placeholder="you@example.com"
                                className="w-full p-3 border border-blueBorder bg-transparent placeholder-placeHolder"
                            />

                            {/* company */}
                            <input
                                type="text"
                                value={company}
                                onChange={(e) => setCompany(e.target.value)}
                                placeholder="Company / Fund name"
                                className="w-full p-3 border border-blueBorder bg-transparent placeholder-placeHolder"
                            />

                            {/* message */}
                            <textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                required
                                rows={4}
                                placeholder="Tell us about your project..."
                                className="w-full p-3 border border-blueBorder bg-transparent placeholder-placeHolder resize-none"
                            />

                            {/* investor checkbox */}
                            <label className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    checked={investor}
                                    onChange={() => {
                                        setInvestor(true);
                                        setPartner(false); // uncheck other
                                    }}
                                    className="h-5 w-5 blueBorder accent-yellow"
                                />
                                <span>I'm an investor. I'd like to receive a pitch deck.</span>
                            </label>

                            {/* partner checkbox */}
                            <label className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    checked={partner}
                                    onChange={() => {
                                        setPartner(true);
                                        setInvestor(false); // uncheck other
                                    }}
                                    className="h-5 w-5 blueBorder accent-yellow"
                                />
                                <span>I'm a potential partner. Please send me a presentation.</span>
                            </label>
                            {/* Turnstile widget */}
                            <div className="flex justify-left items-center gap-10">
                                {/* ---------- Send button ---------- */}
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="h-14 px-10 bg-yellow text-black font-semibold rounded transition-colors
               disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="animate-spin h-5 w-5 border-b-2 border-black mr-2" />
                                            Sending…
                                        </>
                                    ) : (
                                        "Send"
                                    )}
                                </button>

                                {/* ---------- Turnstile wrapper ---------- */}
                                <div className="flex left h-[65px]">   {/* 65 px = normal widget height */}
                                    <Turnstile
                                        ref={turnstileRef}
                                        sitekey={SITE_KEY}
                                        onVerify={setToken}
                                        onExpire={() => setToken("")}
                                        onError={() => setToken("")}
                                        theme="light"
                                        /* size="compact"  // uncomment for 40 px‑tall widget */
                                    />
                                </div>
                            </div>

                        </form>
                    )}
                </div>
            </div>
        </section>
    );
}
